import { Organization } from "@/data/Organization";
import { CurrentUser } from "@/data/users/CurrentUser";
import { AppPages } from "@/router";
import {
  Collapse,
  Divider,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  SxProps,
  Tab,
  Tabs
} from "@mui/material";
import { Tooltip, Utils } from "klayowebshared";
import { Component } from "react";
import { Location, NavLink } from "react-router-dom";
import { AppContext } from "../common/AppContext";
import { withLocale, WithLocaleProps } from "../providers/LocaleProvider";
import RightArrowIcon from "../resources/images/icons-arrow-right.svg";
import ChevronDownIcon from "../resources/images/icons-chevron-down.svg";
import ChevronUpIcon from "../resources/images/icons-chevron-up.svg";

interface AppNavProps extends WithLocaleProps {
  items?: any;
  currentPage?: Partial<(typeof AppPages)[keyof typeof AppPages]>;
  getChildNavItems?: (
    page: Partial<(typeof AppPages)[keyof typeof AppPages]>
  ) => Partial<(typeof AppPages)[keyof typeof AppPages]>[] | undefined;
  drawerMode?: boolean;
  showIcons?: boolean;
  showNavChildren?: boolean;
  autoCollapse?: boolean;
  maxLabelLength?: number;
  itemState?: any;
  onNavigation?: (e: React.MouseEvent, callback?: any) => void;
  onDrawerClose?: (e: React.MouseEvent) => void;
  onClose?: (e: React.MouseEvent) => void;
  onSettings?: (e: React.MouseEvent) => void;
  allowSettings?: boolean;
  onSignOut?: (e: React.MouseEvent) => void;
  onSwitchUser?: (e: React.MouseEvent) => void;
  onDemoSwitch?: (e: React.MouseEvent, toDemo: boolean) => void;
  user?: CurrentUser | null;
  organization?: Organization | null;
  defaultItem?: any;
  sx?: SxProps;
  location?: Location;
  open?: boolean;
  devMode?: boolean;
  allowSwitchUser?: boolean;
  onDevOptions?: (e: React.MouseEvent) => void;
  allowDemoSwitch?: boolean;
}

interface AppNavState {
  currentPage: any;
}

class AppNavBase extends Component<AppNavProps, AppNavState> {
  static contextType = AppContext;

  static defaultProps = {
    drawerMode: false,
    showIcons: false,
    showNavChildren: true,
    autoCollapse: true,
    maxLabelLength: 22
  };

  constructor(props: any) {
    super(props);

    this.state = {
      //collapsed: props.itemState ?? [],
      //childItems: [],
      currentPage: props.currentPage
    };
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    const { autoCollapse, currentPage, items } = this.props;

    if (autoCollapse && prevProps.currentPage !== currentPage && currentPage) {
      if (items)
        items.forEach((page: any, index: any) => {
          this.collapseItem(page, page === currentPage, false);
        });

      this.forceUpdate();
    }
  }

  updateNav() {
    const { itemState, items, currentPage, getChildNavItems } = this.props;
    const { collapsed, childItems } = itemState;

    let update = false;
    if (items)
      items.forEach((page: any, index: any) => {
        if (page.hasChildren && childItems[page.slug] === undefined) {
          childItems[page.slug] = getChildNavItems?.(page);
          if (childItems[page.slug]) {
            update = true;
            if (page === currentPage)
              collapsed[page.slug] = childItems[page.slug] && childItems[page.slug].length > 0;
          }
        }
      });

    if (update) this.forceUpdate();
  }

  onItemClick(e: any) {
    const { drawerMode, onDrawerClose, onNavigation } = this.props;
    if (onNavigation) onNavigation(e);
    if (drawerMode) onDrawerClose?.(e);
  }

  isCollapsed(page: any) {
    const { itemState } = this.props;
    const { collapsed } = itemState;
    return collapsed[page.slug] ?? false;
  }

  collapseItem(page: any, collapse?: any, update = true) {
    const { itemState, getChildNavItems, currentPage } = this.props;
    const { collapsed, childItems } = itemState;

    collapsed[page.slug] =
      collapse !== undefined
        ? collapse
        : collapsed[page.slug] === undefined
          ? true
          : !collapsed[page.slug];

    if (page.hasChildren && childItems[page.slug] === undefined) {
      childItems[page.slug] = getChildNavItems?.(page);
      if (collapse)
        collapsed[page.slug] = childItems[page.slug] && childItems[page.slug].length > 0;
    }
    if (update) this.forceUpdate();
  }

  onCollapseItem(e: any, page: any) {
    e.preventDefault();
    this.collapseItem(page);
  }

  getChildItemLabel(item: any) {
    const { translate } = this.props;
    // Use labelKey for translation if available, otherwise fall back to label
    return item.labelKey ? translate(item.labelKey) : item.label;
  }

  renderChildItems(page: any) {
    const { itemState, maxLabelLength } = this.props;
    const { childItems } = itemState;

    if (!childItems[page.slug]) return null;

    return (
      <List className='klayo-appdrawer_childlist'>
        {childItems[page.slug].map((item: any, i: any) => {
          const label = this.getChildItemLabel(item);
          return (
            <Tooltip
              key={`tt-${i}`}
              title={label.length > (maxLabelLength || 0) ? label : null}
              placement='right'
            >
              <ListItem
                key={i}
                className='klaro-appdrawer_menuitem klaro-appdrawer_childmenuitem'
                to={item.path}
                component={NavLink}
                onClick={this.onItemClick.bind(this)}
              >
                <ListItemText disableTypography={true}>
                  {Utils.truncate(label, maxLabelLength)}
                </ListItemText>
              </ListItem>
            </Tooltip>
          );
        })}
      </List>
    );
  }

  getPageName(page: any) {
    const { translate } = this.props;
    // Use nameKey for translation if available, otherwise fall back to name
    return page.nameKey ? translate(page.nameKey) : page.name;
  }

  renderItems(items: any) {
    const { user, organization, drawerMode, showIcons, showNavChildren, currentPage } = this.props;

    return items.map((page: any, index: any) => {
      const slug = page.getSlug ? page.getSlug(user, organization) : page.slug;
      const pageName = this.getPageName(page);
      return drawerMode ? (
        <div key={index}>
          {page.seperate === true && <Divider />}

          <ListItem
            key={index}
            className={
              "klaro-appdrawer_menuitem" +
              (page.enabled === false ? " klaro-appdrawer_menuitem--hidden" : "") +
              (page.hasChildren === true ? " klaro-appdrawer_parentmenuitem" : "") +
              // Highlight the active section even when NavLink's URL match misses — e.g. the default
              // page (My Profile) served at '/'. currentPage is the top-level section object (matched
              // by the first path segment), so identity holds for nested routes too.
              (page === currentPage ? " active" : "")
            }
            to={"/" + slug}
            component={NavLink}
            onClick={
              page.hasChildren !== true
                ? this.onItemClick.bind(this)
                : (e) => this.onCollapseItem(e, page)
            }
          >
            {showIcons && <ListItemIcon>{page.icon}</ListItemIcon>}
            <ListItemText disableTypography={true}>{pageName}</ListItemText>
            {page.hasChildren === true && (
              <div className='klaro-appdrawer_menuitem_collapsebutton'>
                {showNavChildren ? (
                  this.isCollapsed(page) ? (
                    <ChevronUpIcon />
                  ) : (
                    <ChevronDownIcon />
                  )
                ) : (
                  <RightArrowIcon />
                )}
              </div>
            )}
          </ListItem>

          {page.hasChildren === true && showNavChildren && (
            <Collapse in={this.isCollapsed(page)} timeout='auto' unmountOnExit>
              <List component='div' disablePadding>
                {this.renderChildItems(page)}
              </List>
            </Collapse>
          )}
        </div>
      ) : (
        <Fade in={true} key={index}>
          <Tab
            className={
              "klaro-appbar_nav-tab" +
              (page.enabled === false ? " klaro-appbar_nav-tab--hidden" : "")
            }
            value={page.slug}
            to={"/" + slug}
            label={pageName}
            disableRipple={true}
            onClick={this.onItemClick.bind(this)}
            component={NavLink}
          />
        </Fade>
      );
    });
  }

  render() {
    const { items, defaultItem, sx, drawerMode, currentPage } = this.props;

    return drawerMode ? (
      <div className='klaro-appdrawer-menu'>{this.renderItems(items)}</div>
    ) : (
      <Tabs
        className='klaro-appbar_nav'
        value={currentPage ? currentPage.slug : defaultItem.slug}
        centered={true}
        color='secondary'
        sx={sx}
        TabIndicatorProps={{
          sx: {
            opacity: currentPage ? 1 : 0
          }
        }}
      >
        {this.renderItems(items)}
      </Tabs>
    );
  }
}

export const AppNav = withLocale(AppNavBase);
